import * as React from "react"
import useSession from "@digits-shared/hooks/useSession"
import Session from "@digits-shared/session/Session"
import { type DigitsTheme, Theme, THEME_PREFERENCE_KEY } from "@digits-shared/themes/theme"
import { ThemeContext } from "styled-components"

/*
 COMPONENT
*/

export const ThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const session = useSession()
  const preference = session.getUserPreference?.(THEME_PREFERENCE_KEY) as Theme

  const [mode, setMode] = React.useState<Theme>(preference || Theme.Dark)
  const [context, setContext] = React.useState<DigitsTheme>({ mode, setMode })

  const onThemeChange = React.useCallback(
    (e: StorageEvent) => {
      if (e.key !== Session.PREFERENCES_STORAGE_NAMESPACE) return
      const newMode = session.getUserPreference?.(THEME_PREFERENCE_KEY) as Theme
      if (newMode) {
        setMode(newMode)
      }
    },
    [session]
  )

  const onSessionChange = React.useCallback(() => {
    const newMode = session.getUserPreference?.(THEME_PREFERENCE_KEY) as Theme
    if (newMode) {
      setMode(newMode)
      return
    }

    setMode(Theme.Dark)
  }, [session])

  // Changes on other tabs
  React.useEffect(() => {
    window.addEventListener("storage", onThemeChange)
    return () => {
      window.removeEventListener("storage", onThemeChange)
    }
  }, [onThemeChange])

  // Changes on current tabs
  React.useEffect(() => {
    if (context.mode !== mode) {
      setContext({ mode, setMode })
    }
  }, [session.user, context, mode, setContext])

  // Session changes (login / out)
  React.useEffect(() => {
    session.on?.(Session.JWT_CREATE_EVENT_NAME, onSessionChange)
    session.on?.(Session.JWT_UPDATE_EVENT_NAME, onSessionChange)
    return () => {
      session.off?.(Session.JWT_CREATE_EVENT_NAME, onSessionChange)
      session.off?.(Session.JWT_UPDATE_EVENT_NAME, onSessionChange)
    }
  })

  return <ThemeContext.Provider value={context}>{children}</ThemeContext.Provider>
}
